import { useCallback, useEffect, useMemo, useRef } from "react";
import { atom } from "nanostores";

import { useStore } from "@nanostores/react";
import {
  useGetAllPermissionsByUserId,
  useGetPermissionsForUser,
  useGetRolesNew,
  useRevokeRole,
  useUpdateUserAndPermissions,
} from "@vega-api/generated/auth-permission-v1/auth-permission-v1";
import { type Permission } from "@vega-api/generated/types/permission";
import { type RoleContext } from "@vega-api/generated/types/roleContext";
import { type RoleNew } from "@vega-api/generated/types/roleNew";
import { type RolesResponseNew } from "@vega-api/generated/types/rolesResponseNew";
import { SubjectType } from "@vega-api/generated/types/subjectType";
import { type UserPermissionEntry } from "@vega-api/generated/types/userPermissionEntry";
import { type UserPermissionUpdateRequestEntry } from "@vega-api/generated/types/userPermissionUpdateRequestEntry";
import { useStatusPopup } from "@vega-core/hooks/useStatusPopup";
import { m } from "@vega-core/paraglide/messages";
import { isGPPortal } from "@vega-core/utils/global";
import { handleStoreHMR } from "@vega-core/utils/handleStoreHMR";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

type RoleMap = Record<string, RoleNew>;

type PermissionMap = Record<string, Permission>;

// Current User Store
const $currentUserRolesAtom = atom<RolesResponseNew | null>(null);
const $currentUserPermissionsAtom = atom<PermissionMap | undefined>();
const $currentUserIsLoadingAtom = atom<boolean>(false);
const $currentUserIsFetchedAtom = atom<boolean>(false);
const $currentUserManageablePermissionsAtom = atom<UserPermissionEntry[]>([]);

handleStoreHMR($currentUserRolesAtom, "$currentUserRolesAtom");
handleStoreHMR($currentUserPermissionsAtom, "$currentUserPermissionsAtom");
handleStoreHMR($currentUserIsLoadingAtom, "$currentUserIsLoadingAtom");
handleStoreHMR($currentUserIsFetchedAtom, "$currentUserIsFetchedAtom");
handleStoreHMR(
  $currentUserManageablePermissionsAtom,
  "$currentUserManageablePermissionsAtom",
);

// Target User Store - now keyed by subjectId for isolation
const $targetUserRolesAtom = atom<RolesResponseNew | null>(null);
// Store permissions per subjectId to isolate each fund's permission state
const $targetUserPermissionsAtom = atom<Record<string, PermissionMap>>({});
const $targetUserSelectedRoleAtom = atom<RoleNew | undefined>();
const $targetUserIsLoadingAtom = atom<boolean>(false);
const $targetUserTogglingPermissionAtom = atom<string | null>(null);
const $targetUserIsFetchedAtom = atom<boolean>(false);

handleStoreHMR($targetUserRolesAtom, "$targetUserRolesAtom");
handleStoreHMR($targetUserPermissionsAtom, "$targetUserPermissionsAtom");
handleStoreHMR($targetUserSelectedRoleAtom, "$targetUserSelectedRoleAtom");
handleStoreHMR($targetUserIsLoadingAtom, "$targetUserIsLoadingAtom");
handleStoreHMR(
  $targetUserTogglingPermissionAtom,
  "$targetUserTogglingPermissionAtom",
);
handleStoreHMR($targetUserIsFetchedAtom, "$targetUserIsFetchedAtom");

// Add new atoms for filtered permissions
const $filteredPermissionsAtom = atom<UserPermissionEntry[]>([]);
const $isLoadingFilteredPermissionsAtom = atom<boolean>(false);

handleStoreHMR($filteredPermissionsAtom, "$filteredPermissionsAtom");
handleStoreHMR(
  $isLoadingFilteredPermissionsAtom,
  "$isLoadingFilteredPermissionsAtom",
);

export const createRoleMapById = (roles: RolesResponseNew): RoleMap => {
  const roleMap: RoleMap = {};
  roles.forEach((role) => {
    roleMap[role.id] = role;
  });

  return roleMap;
};

export const getRolePermissionsState = (
  subjectId: string,
  roleId: string,
  rolePermissions: Permission[],
  userPermissions: UserPermissionEntry[] = [],
): PermissionMap => {
  const permissionsMap: PermissionMap = {};
  const userPermissionsMap = new Map<string, UserPermissionEntry>();

  userPermissions.forEach((permission) => {
    if (
      permission.subjectId === subjectId &&
      permission.roleId === roleId &&
      permission.permissionId
    ) {
      userPermissionsMap.set(permission.permissionId, permission);
    }
  });

  rolePermissions.forEach((rolePermission) => {
    const userPermission = userPermissionsMap.get(rolePermission.id);
    permissionsMap[rolePermission.id] = {
      ...rolePermission,
      enabled: userPermission?.permissionEnabled ?? rolePermission.enabled,
    };
  });

  return permissionsMap;
};

export const prepareUserPermissionsPayload = (
  userId: string,
  roleId: string,
  subjectId: string,
  rolePermissions: Permission[],
): UserPermissionUpdateRequestEntry[] =>
  rolePermissions.map((permission) => ({
    userId,
    roleId,
    subjectId,
    permissionId: permission.id,
    permissionEnabled: permission.enabled,
  }));

// Current User Actions
export const currentUserRolePermissionActions = {
  setRoles: (roles: RolesResponseNew | null) => {
    $currentUserRolesAtom.set(roles);
  },
  setPermissions: (permissions: PermissionMap | undefined) => {
    $currentUserPermissionsAtom.set(permissions);
  },
  setIsLoading: (loading: boolean) => {
    $currentUserIsLoadingAtom.set(loading);
  },
  setIsFetched: (isFetched: boolean) => {
    $currentUserIsFetchedAtom.set(isFetched);
  },
  setManageablePermissions: (permissions: UserPermissionEntry[]) => {
    $currentUserManageablePermissionsAtom.set(permissions);
  },
  getRoleById: (roleId: string): RoleNew | undefined => {
    const roles = $currentUserRolesAtom.get();

    if (!roles) return undefined;

    return createRoleMapById(roles)[roleId];
  },
};

// Target User Actions
export const targetUserRolePermissionActions = {
  setRoles: (roles: RolesResponseNew | null) => {
    $targetUserRolesAtom.set(roles);
  },
  setSelectedRole: (role: RoleNew | undefined) => {
    $targetUserSelectedRoleAtom.set(role);
  },
  setPermissions: (
    subjectId: string,
    permissions: PermissionMap | undefined,
  ) => {
    const current = $targetUserPermissionsAtom.get();

    if (permissions === undefined) {
      // Remove permissions for this subject
      const rest = Object.fromEntries(
        Object.entries(current).filter(([key]) => key !== subjectId),
      );
      $targetUserPermissionsAtom.set(rest);
    } else {
      $targetUserPermissionsAtom.set({
        ...current,
        [subjectId]: permissions,
      });
    }
  },
  getPermissions: (subjectId: string): PermissionMap | undefined => {
    const allPermissions = $targetUserPermissionsAtom.get();

    return allPermissions[subjectId];
  },
  setIsLoading: (loading: boolean) => {
    $targetUserIsLoadingAtom.set(loading);
  },
  setTogglingPermission: (permissionId: string | null) => {
    $targetUserTogglingPermissionAtom.set(permissionId);
  },
  setIsFetched: (isFetched: boolean) => {
    $targetUserIsFetchedAtom.set(isFetched);
  },
  initPermissionsState: (
    subjectId: string,
    role: RoleNew,
    filteredPermissions: UserPermissionEntry[] = [],
  ) => {
    const permissions = getRolePermissionsState(
      subjectId,
      role.id,
      role.permissions,
      filteredPermissions,
    );
    const current = $targetUserPermissionsAtom.get();
    $targetUserPermissionsAtom.set({
      ...current,
      [subjectId]: permissions,
    });
  },
  getRoleById: (roleId: string): RoleNew | undefined => {
    const roles = $targetUserRolesAtom.get();

    if (!roles) return undefined;

    return createRoleMapById(roles)[roleId];
  },
};

// Add new actions for filtered permissions
export const filteredPermissionsActions = {
  setFilteredPermissions: (permissions: UserPermissionEntry[]) => {
    $filteredPermissionsAtom.set(permissions);
  },
  setIsLoadingFilteredPermissions: (loading: boolean) => {
    $isLoadingFilteredPermissionsAtom.set(loading);
  },
};

// Target User Hook
export const useTargetUserRolePermission = (
  targetUserId: string,
  subjectId: string,
  roleId: string,
  filteredPermissions: UserPermissionEntry[] = [],
  roleContext?: RoleContext,
) => {
  const { setError } = useStatusPopup();
  const previousPermissionsRef = useRef<PermissionMap>(undefined);

  const roles = useStore($targetUserRolesAtom);
  const allPermissions = useStore($targetUserPermissionsAtom);
  const currentPermissions = allPermissions[subjectId];
  const togglingPermission = useStore($targetUserTogglingPermissionAtom);
  const isFetched = useStore($targetUserIsFetchedAtom);
  const isLoading = useStore($targetUserIsLoadingAtom);

  const { data: rolesData, isLoading: isRolesLoading } = useGetRolesNew(
    roleContext ? { context: roleContext } : undefined,
    {
      query: { select: (response) => response.data },
    },
  );

  useEffect(() => {
    if (rolesData && !isFetched) {
      targetUserRolePermissionActions.setRoles(rolesData);
      targetUserRolePermissionActions.setIsFetched(true);
    }
  }, [rolesData, isFetched]);

  const { mutate: updateUserAndPermissions } = useUpdateUserAndPermissions({
    mutation: {
      onError: (error) => {
        const errorMessage = error.response?.statusText || m.errors_unknown();
        setError({
          title: m.userManagement_permissions_statusPermissionsChangeError(),
          description: errorMessage,
        });
      },
    },
  });

  const { mutate: revokeRole } = useRevokeRole({
    mutation: {
      onError: () => {
        setError({
          title: m.userManagement_permissions_statusRevokeError(),
        });
      },
    },
  });

  const currentRole = useMemo(() => {
    if (!roles || !roleId) return undefined;

    return targetUserRolePermissionActions.getRoleById(roleId);
  }, [roles, roleId]);

  const permissions = useMemo(() => {
    if (!currentRole) return undefined;

    return getRolePermissionsState(
      subjectId,
      currentRole.id,
      currentRole.permissions,
      filteredPermissions,
    );
  }, [currentRole, subjectId, filteredPermissions]);

  useEffect(() => {
    if (permissions) {
      const currentPermissionsStr = JSON.stringify(
        previousPermissionsRef.current,
      );
      const newPermissionsStr = JSON.stringify(permissions);

      if (currentPermissionsStr !== newPermissionsStr) {
        previousPermissionsRef.current = permissions;
        targetUserRolePermissionActions.setPermissions(subjectId, permissions);
      }
    }
  }, [permissions, subjectId]);

  const isEdited = useMemo(() => {
    if (isFalsy(currentPermissions) || isFalsy(currentRole)) {
      return false;
    }

    return currentRole.permissions.some((rolePermission) => {
      const currentPermission = currentPermissions[rolePermission.id];

      return (
        isTruthy(currentPermission) &&
        rolePermission.enabled !== currentPermission.enabled
      );
    });
  }, [currentRole, currentPermissions]);

  const handlePermissionToggle = useCallback(
    (permissionId: string, isEnabled: boolean) => {
      if (isFalsy(currentRole) || isFalsy(currentPermissions)) {
        return;
      }

      const userPermissionsUpdated = {
        ...currentPermissions,
        [permissionId]: {
          ...currentPermissions[permissionId],
          enabled: isEnabled,
        },
      };

      const previousPermissions = currentPermissions;
      targetUserRolePermissionActions.setPermissions(
        subjectId,
        userPermissionsUpdated,
      );
      targetUserRolePermissionActions.setTogglingPermission(permissionId);

      const permissions = Object.values(userPermissionsUpdated);

      const userPermissions = prepareUserPermissionsPayload(
        targetUserId,
        currentRole.id,
        subjectId,
        permissions,
      );

      updateUserAndPermissions(
        {
          data: {
            entries: userPermissions,
          },
        },
        {
          onError: (error: unknown) => {
            if ((error as { status?: number }).status !== 401) {
              targetUserRolePermissionActions.setPermissions(
                subjectId,
                previousPermissions,
              );
            }
          },
          onSettled: () => {
            targetUserRolePermissionActions.setTogglingPermission(null);
          },
        },
      );
    },
    [
      currentPermissions,
      currentRole,
      subjectId,
      targetUserId,
      updateUserAndPermissions,
    ],
  );

  const handleReset = useCallback(
    (role: RoleNew) => {
      const permissions = getRolePermissionsState(
        subjectId,
        role.id,
        role.permissions,
      );
      targetUserRolePermissionActions.setPermissions(subjectId, permissions);

      const userPermissions = prepareUserPermissionsPayload(
        targetUserId,
        role.id,
        subjectId,
        role.permissions,
      );

      targetUserRolePermissionActions.setIsLoading(true);
      updateUserAndPermissions(
        {
          data: {
            entries: userPermissions,
          },
        },
        {
          onError: () => {
            targetUserRolePermissionActions.initPermissionsState(
              subjectId,
              role,
              filteredPermissions,
            );
          },
          onSettled: () => {
            targetUserRolePermissionActions.setIsLoading(false);
          },
        },
      );
    },
    [subjectId, targetUserId, updateUserAndPermissions, filteredPermissions],
  );

  const handleDeleteRole = useCallback(
    (roleId: string) => {
      targetUserRolePermissionActions.setIsLoading(true);
      revokeRole({
        roleId,
        params: {
          userId: targetUserId,
          subjectId,
        },
      });
    },
    [subjectId, targetUserId, revokeRole],
  );

  // Get permissions specific to this subject/fund
  const subjectPermissions = useMemo(() => {
    if (!currentRole) return [];

    // Filter to get only permissions for this specific subject/fund
    return filteredPermissions.filter(
      (permission) =>
        permission.subjectId === subjectId &&
        permission.roleId === currentRole.id,
    );
  }, [filteredPermissions, subjectId, currentRole]);

  return {
    isLoading: isLoading || isRolesLoading,
    currentPermissions,
    selectedRole: currentRole,
    isEdited,
    handlePermissionToggle,
    handleReset,
    handleDeleteRole,
    togglingPermission,
    isFetched,
    permissions: currentRole?.permissions || [],
    subjectPermissions,
  };
};

// Add new hook for filtered permissions
export const useFilteredPermissions = (userId: string) => {
  const {
    data: allPermissions = [],
    isLoading: isLoadingAllPermissions,
    refetch: refetchAllPermissions,
  } = useGetAllPermissionsByUserId(userId);

  const {
    data: basicPermissions = [],
    isLoading: isLoadingBasicPermissions,
    refetch: refetchBasicPermissions,
  } = useGetPermissionsForUser(userId, {
    query: {
      select: (response) => response.entries,
    },
  });

  const filteredPermissions = useStore($filteredPermissionsAtom);
  const isLoadingFilteredPermissions = useStore(
    $isLoadingFilteredPermissionsAtom,
  );

  const userPermissions = useMemo(
    () => (isGPPortal ? allPermissions : basicPermissions),
    [allPermissions, basicPermissions],
  );

  const entries = useMemo(
    () => (isGPPortal ? userPermissions.entries : userPermissions),
    [userPermissions],
  );

  useEffect(() => {
    let filtered: UserPermissionEntry[] = [];

    if (isTruthy(entries) && Array.isArray(entries)) {
      filtered = entries.filter(
        (permission: UserPermissionEntry) =>
          "subjectType" in permission &&
          permission.subjectType === SubjectType.INVESTMENT,
      );
    }

    const current = $filteredPermissionsAtom.get();

    if (
      filtered.length !== current.length ||
      JSON.stringify(filtered) !== JSON.stringify(current)
    ) {
      filteredPermissionsActions.setFilteredPermissions(filtered);
    }
  }, [entries]);

  useEffect(() => {
    const isLoading = isGPPortal
      ? isLoadingAllPermissions
      : isLoadingBasicPermissions;
    filteredPermissionsActions.setIsLoadingFilteredPermissions(isLoading);
  }, [isLoadingAllPermissions, isLoadingBasicPermissions]);

  return {
    filteredPermissions,
    isLoadingFilteredPermissions,
    refetchFilteredPermissions: isGPPortal
      ? refetchAllPermissions
      : refetchBasicPermissions,
  };
};
